import { ChangeDetectionStrategy, Component, ElementRef, OnInit, ViewChild } from '@angular/core';
import { FormControl, ReactiveFormsModule } from '@angular/forms';
import { CommonModule } from '@angular/common';
import { BaseDestroyableComponent } from '@shared/base-classes/base-destroyable';
import { merge, Observable } from 'rxjs';
import { takeUntil, tap, map, shareReplay, switchMap } from 'rxjs/operators';
import { Procedure } from '@shared/models/procedure';
import { RxIntuitiveOrderFacade } from './rx-intuitive-order.facade';
import { OrderFormConfirmationService } from '@modules/order/services/order-form-confirmation.service';
import { FavoritesEnum } from '@shared/models/enums/favorites.enum';
import { RoleTypeEnum } from '@shared/models/role-type';
import { SendToOption } from '@shared/models/send-to-option';
import { SharedModule } from '@shared/shared.module';
import { TranslateModule } from '@ngx-translate/core';
import { TlkCardComponent } from '@itero/ui-toolkit-angular/card';
import { DueDatePickerComponent } from '@modules/order/components/due-date-picker/due-date-picker.component';
import { RxProcedureButtonComponent } from '@shared/components/rx-procedure-button/rx-procedure-button.component';
import { RxProcedureTilesComponent } from '@shared/components/rx-procedure-tiles/rx-procedure-tiles.component';
import { RxIntuitiveOrderLayoutComponent } from '@shared/components/rx-intuitive-order-layout/rx-intuitive-order-layout.component';
import { TlkExpansionAreaComponent, TlkExpansionContentDirective } from '@itero/ui-toolkit-angular/expansion-area';
import { FormControlStatus } from '@shared/models/form-control-status';
import { TeethDiagramFacade } from '@modules/teeth-diagram/teeth-diagram.facade';
import { RxIntuitiveScanOptionsFacade } from '@modules/rx-intuitive-scan-options/rx-intutive-scan-options.facade';
import { ProcedureTypeService } from '@shared/components/rx-procedure-type-control/procedure-type.service';

@Component({
	selector: 'rx-intuitive-order',
	templateUrl: './rx-intuitive-order.component.html',
	styleUrls: ['./rx-intuitive-order.component.scss'],
	providers: [
		RxIntuitiveOrderFacade,
		TeethDiagramFacade,
		RxIntuitiveScanOptionsFacade,
		OrderFormConfirmationService,
		ProcedureTypeService
	],
	changeDetection: ChangeDetectionStrategy.OnPush,
	standalone: true,
	imports: [
		CommonModule,
		ReactiveFormsModule,
		SharedModule,
		TranslateModule,
		TlkCardComponent,
		DueDatePickerComponent,
		RxProcedureButtonComponent,
		RxProcedureTilesComponent,
		TlkExpansionAreaComponent,
		TlkExpansionContentDirective,
		RxIntuitiveOrderLayoutComponent
	]
})
export class RxIntuitiveOrderComponent extends BaseDestroyableComponent implements OnInit {
	@ViewChild('expansionPanel') expansionPanelRef: ElementRef<HTMLElement>;

	private isReadOnly: boolean;

	isReadOnly$: Observable<boolean> = this.facade.isReadOnly$.pipe(
		tap(isReadOnly => (this.isReadOnly = isReadOnly)),
		shareReplay({ refCount: true, bufferSize: 1 })
	);

	isProcedureSelectionReadOnly$: Observable<boolean> = this.facade.isProcedureSelectionReadOnly$;

	procedures$ = this.facade.procedureTiles$;
	availableLabs$ = this.facade.availableLabs$;
	minimalAvailableDueDate$ = this.facade.minimalAvailableDueDate$;

	sendToState$: Observable<FormControlStatus> = this.facade.sendToState$;
	dueDateState$: Observable<FormControlStatus> = this.facade.dueDateState$;
	isSendToCancellable$ = this.facade.isSendToCancellable$;

	// Procedure observables
	selectedProcedureId$ = this.facade.selectedProcedureId$;
	selectedProcedure$ = this.facade.selectedProcedureTile$;
	canChangeProcedure$ = this.procedures$.pipe(map(procedures => procedures.length > 1));

	proceduresSelectExpanded = false;

	// Individual form controls
	sendToControl = new FormControl<SendToOption | null>(null);
	dueDateControl = new FormControl<Date | undefined>(undefined);

	favoriteSendTo = FavoritesEnum.SendTo;
	isNotDoctor$: Observable<boolean> = this.facade.userRole$.pipe(
		map(userRole => userRole !== RoleTypeEnum.Doctor),
		shareReplay({ bufferSize: 1, refCount: true })
	);

	constructor(private readonly facade: RxIntuitiveOrderFacade) {
		super();
	}

	ngOnInit(): void {
		this.subscribeToRxLoaded();
		this.subscribeToUserChanges();
	}

	clearControl(control: FormControl) {
		if (!this.isReadOnly) {
			control.patchValue(null);
		}
	}

	showSelectPanel(): void {
		this.proceduresSelectExpanded = !this.proceduresSelectExpanded;
	}

	onProcedureChanged(procedureId: Procedure['Id']): void {
		this.facade.handleProcedureChanged(procedureId);
		this.proceduresSelectExpanded = false;
	}

	onChangeProcedureCancelled(): void {
		this.proceduresSelectExpanded = false;
	}

	private subscribeToRxLoaded(): void {
		this.facade.sendTo$
			.pipe(
				tap(sendTo => this.sendToControl.setValue(sendTo, { emitEvent: false })),
				takeUntil(this.componentAlive$)
			)
			.subscribe();

		this.facade.dueDate$
			.pipe(
				tap(dueDate => this.dueDateControl.setValue(dueDate, { emitEvent: false })),
				takeUntil(this.componentAlive$)
			)
			.subscribe();
	}

	private subscribeToUserChanges(): void {
		merge(
			this.sendToControl.valueChanges.pipe(
				switchMap((sendTo: SendToOption) => this.facade.handleSendToChanged(sendTo)),
				tap(result => this.sendToControl.patchValue(result, { emitEvent: false }))
			),
			this.dueDateControl.valueChanges.pipe(tap((dueDate: Date) => this.facade.handleDueDateChanged(dueDate)))
		)
			.pipe(takeUntil(this.componentAlive$))
			.subscribe();
	}
}
